import React, { useState, useEffect, useRef } from 'react';
import { 
  ChevronRight, 
  MapPin, 
  ShieldCheck, 
  Lock, 
  Zap,
  Users,
  ArrowRight
} from 'lucide-react';

// Custom Hook for Scroll Animations
const useIntersectionObserver = (options = {}) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const targetRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsIntersecting(true);
        if (options.triggerOnce) observer.unobserve(entry.target);
      }
    }, options);

    if (targetRef.current) observer.observe(targetRef.current);
    return () => observer.disconnect();
  }, [options]);

  return [targetRef, isIntersecting];
};

// Count-up Component for Stats
const CountUp = ({ end, duration = 2000, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const [ref, isVisible] = useIntersectionObserver({ triggerOnce: true, threshold: 0.1 });

  useEffect(() => {
    if (!isVisible) return;
    let startTimestamp = null;
    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };
    window.requestAnimationFrame(step);
  }, [isVisible, end, duration]);

  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
};

const App = () => {
  // Add Tailwind CDN and Google Fonts dynamically to fix the "Plain Text" issue
  useEffect(() => {
    // Inject Tailwind
    if (!document.getElementById('tailwind-cdn')) {
      const script = document.createElement('script');
      script.id = 'tailwind-cdn';
      script.src = 'https://cdn.tailwindcss.com';
      document.head.appendChild(script);
    }

    // Inject Fonts
    if (!document.getElementById('google-fonts')) {
      const link = document.createElement('link');
      link.id = 'google-fonts';
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@700&family=Inter:wght@400;500;600&family=Sora:wght@700;800&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  const scrollToFeatures = (e) => {
    e.preventDefault();
    document.getElementById('how')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#0A0D14] text-white font-['Inter'] selection:bg-[#F5A623] selection:text-black overflow-x-hidden">
      {/* Custom Styles for Animations and Gradients */}
      <style dangerouslySetInnerHTML={{ __html: `
        .font-display { font-family: 'Sora', 'Space Grotesk', sans-serif; }
        
        @keyframes blob {
          0% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0, 0) scale(1); }
        }
        .animate-blob {
          animation: blob 15s infinite alternate ease-in-out;
        }
        
        .glass {
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.08);
        }
        
        .text-gradient {
          background: linear-gradient(to right, #F5A623, #FF6B35);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .btn-glow:hover {
          box-shadow: 0 0 20px rgba(245, 166, 35, 0.4);
        }
      `}} />

      {/* Background Orbs & Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] bg-[#00C896] opacity-[0.12] blur-[120px] animate-pulse"></div>
        <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-[#F5A623] opacity-[0.15] blur-[150px] animate-blob"></div>
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[url('https://grainy-gradients.vercel.app/noise.svg')]"></div>
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 glass border-b border-white/5 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-1 cursor-default">
            <span className="font-display text-2xl font-extrabold tracking-tighter">
              Help<span className="text-[#F5A623]">Me</span><span className="text-[#00C896]">.</span>
            </span>
          </div>

          <div className="text-[10px] font-bold tracking-[0.2em] text-[#8A94A6] uppercase hidden md:block">
            Ground Truth for Africa
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-40 pb-20 px-6 min-h-screen flex flex-col items-center justify-center text-center">
        <div className="max-w-4xl mx-auto space-y-8">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#F5A623]/10 border border-[#F5A623]/20">
            <span className="flex h-2 w-2 rounded-full bg-[#F5A623] animate-ping"></span>
            <span className="text-[#F5A623] text-[10px] font-bold tracking-[0.15em] uppercase">
              ⚡ Coming Soon · Seed Round 2026
            </span>
          </div>

          <h1 className="font-display text-5xl md:text-[80px] leading-[1.1] font-extrabold tracking-tight">
            The <span className="text-gradient">Ground Truth</span> <br />
            Africa Has Been Waiting For.
          </h1>

          <p className="text-[#8A94A6] text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
            HelpMe is a reverse-bounty marketplace where verified citizens compete to deliver real-world intelligence — prices, verification, research — powered by escrow and proof of truth.
          </p>

          <div className="flex flex-col items-center gap-6 pt-4">
            <button 
              onClick={scrollToFeatures}
              className="text-[#00C896] flex items-center gap-2 text-sm font-medium hover:opacity-80 transition-all cursor-pointer"
            >
              Learn how it works <ChevronRight size={16} className="rotate-90" />
            </button>
          </div>

          {/* Abstract Mockup */}
          <div className="mt-20 relative w-full max-w-lg mx-auto p-8 rounded-3xl glass border border-white/10 group hover:border-[#F5A623]/30 transition-all duration-500">
            <div className="space-y-4 text-left">
              <div className="flex justify-between items-start">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-[#00C896] text-xs font-bold uppercase tracking-widest">
                    <Zap size={12} /> Live Bounty
                  </div>
                  <h3 className="text-lg font-semibold">Confirm cement prices in 5 Kano markets</h3>
                </div>
                <div className="text-right">
                  <div className="text-[#F5A623] font-bold text-xl">₦10,000</div>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-4 pt-2">
                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-[10px]">
                  <MapPin size={12} className="text-[#8A94A6]" /> Kano, NG
                </div>
                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#00C896]/10 border border-[#00C896]/20 text-[#00C896] text-[10px] font-semibold">
                  <ShieldCheck size={12} /> Verified Task
                </div>
                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-[10px]">
                  <Lock size={12} className="text-[#8A94A6]" /> Escrow Active
                </div>
              </div>

              <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden mt-4">
                <div className="h-full bg-gradient-to-r from-[#F5A623] to-[#FF6B35] w-2/3"></div>
              </div>
              <div className="text-[10px] text-[#8A94A6] text-center italic">
                3 verified helpers currently on the ground...
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="how" className="py-24 px-6 max-w-7xl mx-auto scroll-mt-24">
        <div className="text-center md:text-left mb-16 space-y-4">
          <span className="text-[#F5A623] text-sm font-bold tracking-[0.15em] uppercase">What We're Building</span>
          <h2 className="font-display text-4xl md:text-5xl font-bold max-w-xl">Post a Bounty. <br />Get the Truth.</h2>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              title: "Post a Bounty",
              desc: "Define your need and set the price. From ₦1,000 to $10,000. Data, verification, research, errands.",
              icon: <ArrowRight className="text-[#F5A623]" />
            },
            {
              title: "Citizens Compete",
              desc: "Verified helpers claim the task. GPS-tagged, time-stamped proof required. No ghost workers.",
              icon: <Users className="text-[#00C896]" />
            },
            {
              title: "Proof of Truth",
              desc: "AI + peer validators verify results. Escrow releases only on approval. 4-layer validation engine.",
              icon: <ShieldCheck className="text-blue-400" />
            }
          ].map((feature, idx) => (
            <div key={idx} className="glass p-10 rounded-2xl group hover:border-[#F5A623]/40 transition-all hover:-translate-y-2 cursor-default">
              <div className="w-12 h-12 mb-6 rounded-lg flex items-center justify-center bg-white/5 group-hover:bg-white/10 transition-colors">
                {feature.icon}
              </div>
              <h3 className="text-xl font-bold mb-4">{feature.title}</h3>
              <p className="text-[#8A94A6] leading-relaxed">
                {feature.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Market Pull Strip */}
      <section className="bg-gradient-to-b from-[#1A1200] to-[#0A0D14] border-y border-white/5 py-16 px-6">
        <div className="max-w-7xl mx-auto grid md:grid-cols-1 lg:grid-cols-3 gap-12 text-center">
          <div className="space-y-2">
            <div className="text-5xl font-display font-extrabold text-[#F5A623]">$<CountUp end={5} suffix="B+" /></div>
            <div className="text-[#8A94A6] text-[10px] leading-tight max-w-[200px] mx-auto uppercase tracking-wider font-semibold">
              Spent annually on African market research — most of it 6 months stale
            </div>
          </div>
          <div className="hidden lg:block w-px h-24 bg-white/10 mx-auto self-center"></div>
          <div className="space-y-2">
            <div className="text-5xl font-display font-extrabold text-[#F5A623]"><CountUp end={20} suffix="M+" /></div>
            <div className="text-[#8A94A6] text-[10px] leading-tight max-w-[200px] mx-auto uppercase tracking-wider font-semibold">
              Nigerians in the diaspora needing eyes on the ground
            </div>
          </div>
          <div className="hidden lg:block w-px h-24 bg-white/10 mx-auto self-center"></div>
          <div className="space-y-2">
            <div className="text-5xl font-display font-extrabold text-[#F5A623]"><CountUp end={60} suffix="%" /></div>
            <div className="text-[#8A94A6] text-[#8A94A6] text-[10px] leading-tight max-w-[200px] mx-auto uppercase tracking-wider font-semibold">
              of Nigerian Gen-Z actively seeking micro-income opportunities
            </div>
          </div>
        </div>
      </section>

      {/* Coming Soon Section */}
      <section className="py-32 px-6 relative overflow-hidden text-center">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-[#F5A623] opacity-[0.1] blur-[150px] pointer-events-none"></div>
        <div className="max-w-3xl mx-auto space-y-8 relative z-10">
          <h2 className="font-display text-4xl md:text-6xl font-bold">African Intelligence.<br />On Demand. In Real Time.</h2>
          <p className="text-[#8A94A6] text-lg max-w-xl mx-auto leading-relaxed">
            HelpMe is currently in private development. <br />
            Launching in Lagos and Abuja in Q2 2026.
          </p>
          <div className="inline-block px-8 py-4 glass border border-[#F5A623]/30 rounded-2xl text-[#F5A623] font-bold uppercase tracking-widest text-sm">
            Access Portal Opening Soon
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-12 px-6">
        <div className="max-w-7xl mx-auto space-y-10">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
            <div className="space-y-2">
              <div className="font-display text-2xl font-extrabold">Help<span className="text-[#F5A623]">Me</span><span className="text-[#00C896]">.</span></div>
              <p className="text-[#8A94A6] text-[10px] font-bold uppercase tracking-[0.2em]">Ground Truth for Africa</p>
            </div>
            
            <div className="flex flex-col md:flex-row gap-8 md:gap-16">
              <div className="space-y-1">
                <span className="text-[#8A94A6] text-[10px] font-bold uppercase">Digital HQ</span>
                <p className="text-sm font-medium opacity-80">helpme.africa</p>
              </div>
              <div className="space-y-1">
                <span className="text-[#8A94A6] text-[10px] font-bold uppercase">Contact</span>
                <p className="text-sm font-medium opacity-80">jibrinx@yahoo.com</p>
              </div>
            </div>
          </div>
          
          <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between gap-4">
            <p className="text-[#8A94A6] text-[10px] uppercase tracking-widest">© 2026 HelpMe. All rights reserved.</p>
            <div className="flex gap-6 text-[10px] text-[#8A94A6] font-bold uppercase tracking-widest">
              <span className="cursor-default">Privacy</span>
              <span className="cursor-default">Terms</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;